import { CMSUProvider } from '@seek/cmsu-components/src/hooks/useCMSUContext';
import {
  Box,
  Column,
  Columns,
  ContentBlock,
  Hidden,
  PageBlock,
  Stack,
  Text,
  ToastProvider,
} from 'braid-design-system';
import { useState, type Dispatch, type SetStateAction } from 'react';
import { useLoaderData } from 'react-router';
import type { GetSurveyQuery } from '../../../gql/tal/generated';
import { PageLayout } from '../../components/PageLayout/PageLayout';
import { useConfig } from '../../shared/hooks';
import { useTargetBlank } from '../../shared/hooks/useTargetBlank';
import type { LayoutProps } from '../../shared/types';
import { DriverSummary } from './components/DriverSummary/DriverSummary';
import type { DriverList } from './components/QueryFilter';
import { QueryFilterDrawer } from './components/QueryFilterDrawer';
import { QueryFilterForm } from './components/QueryFilterForm';
import {
  dataLabHelper,
  type DatalabQueryParams,
  type DatalabSurvey,
} from './datalabHelper';
import { CandidateInformation } from './components/CandidateInformation/CandidateInformation';
import type { CandidateCardProps } from './components/CandidateInformation/CandidateCard';
import { Theme } from '@seek/cmsu-cms-connect';
import { BarChartGroup } from './components/BarChart/BarChartGroup';
import { BarChartItem } from './components/BarChart/BarChartItem';

// TODO!: Replace with actual page title
const pageTitle = 'Talent Acquisition Lab';
const layoutProps: LayoutProps = {
  title: 'Loading...',
};

export const Datalab = () => {
  useTargetBlank();
  // Get the params from context.
  const {
    // melwaysLocale, fullURL, sourceName, privacyUrl, country,
    fullURL,

    language,
  } = useConfig();

  const { queryFilters, surveyResults, queryFilterParams } =
    useLoaderData() as DatalabSurvey;

  const driverList: DriverList = dataLabHelper.getDriverListFrom(queryFilters);
  const [showFilterDrawer, setShowDrawerFilter] = useState<boolean>(false);

  return (
    <PageLayout
      layoutProps={{
        ...layoutProps,
        title: pageTitle,
        section: { theme: Theme.SeekJobs },
      }}
    >
      <CMSUProvider config={{ language }}>
        <ToastProvider>
          <PageBlock width="large">
            <ContentBlock width="large">
              <Box paddingY={'large'}>
                <Columns
                  space={{ mobile: 'medium', tablet: 'xlarge' }}
                  collapseBelow="tablet"
                >
                  <Column width="content">
                    <DatalabNavigation
                      driverList={driverList}
                      queryFilterParams={queryFilterParams}
                      setShowDrawerFilter={setShowDrawerFilter}
                      showFilterDrawer={showFilterDrawer}
                    />
                  </Column>

                  <Column>
                    <Box>
                      <DatalabResult
                        fullURL={fullURL}
                        queryFilterParams={queryFilterParams}
                        surveyResults={surveyResults}
                      />
                    </Box>
                    <Box padding="medium">
                      <Stack space="medium">
                        <BarChartItem
                          value={10}
                          maxValue={100}
                          label="Hello"
                          showInfoBox
                          infoBoxContent={
                            <Text>
                              This % is how candidates rank the driver on
                              importance. All drivers could be
                              &quot;important&quot; but this helps you
                              prioritise the drivers to focus on in job ads or
                              conversations.
                            </Text>
                          }
                        />
                        <BarChartItem
                          value={10}
                          maxValue={100}
                          label="mamamia"
                          width={'90%'}
                          showInfoBox
                          infoBoxContent={
                            <Text>
                              This % is how candidates rank the driver on
                              importance. All drivers could be
                              &quot;important&quot; but this helps you
                              prioritise the drivers to focus on in job ads or
                              conversations.
                            </Text>
                          }
                        />

                        <BarChartGroup
                          label="Male"
                          tooltip={
                            <Text>
                              This is how candidates sort their preferences for
                              the components of Work-life balance. Candidates
                              have the option to choose more than one must-have.
                            </Text>
                          }
                          items={[
                            {
                              label: 'Hello',
                              value: 10,
                              color: '#5B2084',
                            },
                            {
                              label: 'Delighted if',
                              value: 20,
                              color: '#A04BCB',
                            },
                            {
                              label: 'Not bothered',
                              value: 10,

                              color: '#1FA575',
                            },
                            {
                              label: 'Put off',
                              value: 9,

                              color: '#EF672A',
                            },
                          ]}
                        />

                        <BarChartGroup
                          label="Female"
                          tooltip={
                            <Text>
                              This is how candidates sort their preferences for
                              the components of Work-life balance. Candidates
                              have the option to choose more than one must-have.
                            </Text>
                          }
                          items={[
                            {
                              label: 'Hello',
                              value: 13,
                              color: '#5B2084',
                            },
                            {
                              label: 'Delighted if',
                              value: 14,
                              color: '#A04BCB',
                            },
                            {
                              hidden: true,
                              label: 'Not bothered',
                              value: 10,

                              color: '#1FA575',
                            },
                            {
                              label: 'Put off',
                              value: 9,

                              color: '#EF672A',
                            },
                          ]}
                        />
                      </Stack>
                    </Box>
                  </Column>
                </Columns>
              </Box>
            </ContentBlock>
          </PageBlock>
        </ToastProvider>
      </CMSUProvider>
    </PageLayout>
  );
};

interface DatalabNavigationProps {
  driverList: DriverList;
  queryFilterParams: DatalabQueryParams;
  setShowDrawerFilter: Dispatch<SetStateAction<boolean>>;
  showFilterDrawer: boolean;
}

const DatalabNavigation = ({
  driverList,
  queryFilterParams,
  setShowDrawerFilter,
  showFilterDrawer,
}: DatalabNavigationProps) => (
  <>
    <Hidden below="tablet">
      <Box display={'flex'} flexShrink={0} style={{ width: '350px' }}>
        <QueryFilterForm
          driverList={driverList}
          queryFilterParams={queryFilterParams}
        />
      </Box>
    </Hidden>
    <Hidden above="mobile">
      <QueryFilterDrawer
        driverList={driverList}
        queryFilterParams={queryFilterParams}
        setShowFilterDrawer={setShowDrawerFilter}
        showFilterDrawer={showFilterDrawer}
      />
    </Hidden>
  </>
);

interface DatalabResultProps {
  fullURL: string;
  queryFilterParams: DatalabQueryParams;
  surveyResults: GetSurveyQuery;
}

const DatalabResult = ({
  fullURL,
  queryFilterParams,
  surveyResults,
}: DatalabResultProps) => {
  const filteredDriverOptions: string =
    dataLabHelper.generateResultFromFilteredDriverOptionsInQueryParam(
      queryFilterParams,
    );

  const candidateCards: CandidateCardProps[] =
    dataLabHelper.mapAndSortAllSurveyDemographicItems(
      surveyResults.demographic,
    );

  return (
    <Box>
      <Stack space="xlarge">
        <DriverSummary
          pageUrl={fullURL}
          pageTitle={pageTitle}
          filteredDriverOptions={filteredDriverOptions}
          respondentCount={surveyResults.respondents}
          candidateCount={surveyResults.demographic.respondants}
        />
        <Box>
          <Stack space={'xlarge'}>
            <DriverDescription />

            <CandidateInformation candidateCards={candidateCards} />
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

const DriverDescription = () => (
  <Stack space="gutter">
    <Text size="large" weight="strong">
      Drivers that attract these candidates
    </Text>
    <Text>
      These are the drivers candidates consider when looking at roles. Drivers
      are ranked with the most important driver at the top. Expand each driver
      for more detail.
    </Text>
  </Stack>
);
